import React, { useState, useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { LogOut, Download, CheckCircle, MessageCircle, User } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

export const B2BPortalPage = () => {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPartnerInfo = React.useCallback(async () => {
    const token = sessionStorage.getItem('b2b_token');
    const tokenExpiry = sessionStorage.getItem('b2b_token_expiry');
    
    // Check token expiration
    if (!token || (tokenExpiry && Date.now() > parseInt(tokenExpiry))) {
      sessionStorage.clear();
      navigate('/b2b/login');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/portal/info`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPartner(data);
      } else {
        // Token invalid or expired
        sessionStorage.clear();
        navigate('/b2b/login');
      }
    } catch (err) {
      // Silent fail - redirect to login on error
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadPartnerInfo();
  }, [loadPartnerInfo]);

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/b2b/login');
  };

  if (loading) {
    return (
      <div>
        <Navigation />
        <div style={{ padding: '120px 20px', textAlign: 'center' }}>
          <p className="body-large">Loading portal...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <WhatsAppButton variant="floating" />

      {/* Header */}
      <section style={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
        padding: '60px 0 40px',
        color: 'white'
      }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
            <div>
              <h1 className="hero-medium" style={{ color: 'white', marginBottom: '8px' }}>
                B2B Partner Portal
              </h1>
              <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.9)' }}>
                Welcome back, {partner?.company_name}
              </p>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
                Partner ID: {partner?.partner_id}
              </p>
            </div>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                cursor: 'pointer',
                borderRadius: 0
              }}
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </section>

      {/* Exclusive B2B Tariff Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div style={{
              display: 'inline-block',
              padding: '8px 20px',
              background: '#25D366',
              color: 'white',
              fontWeight: 600,
              fontSize: '12px',
              letterSpacing: '1px',
              marginBottom: '16px'
            }}>
              VERIFIED B2B AGENT EXCLUSIVE
            </div>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
              Bali Group Tour - 6 Days / 5 Nights
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Target Market: India / Nepal / Bhutan B2B Travel Agents & Tour Operators
            </p>
          </div>

          {/* Tariff Card */}
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            background: 'var(--bg-secondary)',
            border: '2px solid var(--border-medium)',
            borderRadius: 0
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
              padding: '32px',
              color: 'white',
              textAlign: 'center'
            }}>
              <h3 className="heading-2" style={{ color: 'white', marginBottom: '12px' }}>
                Bali 6D5N Group Package
              </h3>
              <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.9)' }}>
                Travel Dates: 13 – 18 October 2026
              </p>
            </div>

            {/* Package Details */}
            <div style={{ padding: '40px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '32px',
                marginBottom: '40px'
              }}>
                <div>
                  <h4 className="heading-3" style={{ marginBottom: '20px' }}>
                    Package Overview
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <CheckCircle size={18} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                      <span className="body-regular"><strong>Group Size:</strong> 20 Paying Pax + 1 FOC Tour Leader</span>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <CheckCircle size={18} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                      <span className="body-regular"><strong>Accommodation:</strong> 3 Star Standard Hotel (Twin Sharing)</span>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <CheckCircle size={18} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                      <span className="body-regular"><strong>Catering:</strong> 100% Indian Cuisine Only</span>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <CheckCircle size={18} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                      <span className="body-regular">Vegetarian & Non-Vegetarian Options</span>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <CheckCircle size={18} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                      <span className="body-regular">Jain meals available upon request</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="heading-3" style={{ marginBottom: '20px' }}>
                    B2B Net Rates
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{
                      padding: '20px',
                      background: 'var(--bg-primary)',
                      border: '2px solid var(--text-primary)'
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-secondary)' }}>
                        TIER 1 NET RATE
                      </div>
                      <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '4px' }}>
                        USD 575
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                        per person | Total: USD 11,500 (20 pax)
                      </div>
                    </div>
                    <div style={{
                      padding: '20px',
                      background: 'var(--bg-primary)'
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-secondary)' }}>
                        TIER 2 NET RATE
                      </div>
                      <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '4px' }}>
                        USD 625
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                        per person | Total: USD 12,500 (20 pax)
                      </div>
                    </div>
                    <div style={{
                      padding: '12px 16px',
                      background: '#25D366',
                      color: 'white',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: 600
                    }}>
                      + 1 FOC Tour Leader Included
                    </div>
                  </div>
                </div>
              </div>

              {/* Itinerary */}
              <h4 className="heading-2" style={{ marginBottom: '24px' }}>
                6D5N Itinerary Breakdown
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '16px',
                marginBottom: '32px'
              }}>
                <div style={{ padding: '20px', background: 'var(--bg-primary)' }}>
                  <div style={{ fontWeight: 700, marginBottom: '8px' }}>Day 1 (13 Oct)</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Arrival Bali, airport transfer, Indian dinner (if arrival before 19:00)
                  </div>
                </div>
                <div style={{ padding: '20px', background: 'var(--bg-primary)' }}>
                  <div style={{ fontWeight: 700, marginBottom: '8px' }}>Day 2 (14 Oct)</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Uluwatu Temple, Kecak & Fire Dance, Indian meals (B/L/D)
                  </div>
                </div>
                <div style={{ padding: '20px', background: 'var(--bg-primary)' }}>
                  <div style={{ fontWeight: 700, marginBottom: '8px' }}>Day 3 (15 Oct)</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Ubud, Kintamani, Rice Terrace, Coffee Plantation (B/L/D)
                  </div>
                </div>
                <div style={{ padding: '20px', background: 'var(--bg-primary)' }}>
                  <div style={{ fontWeight: 700, marginBottom: '8px' }}>Day 4 (16 Oct)</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Traditional Village, Temple, Handicraft Shopping (B/L/D)
                  </div>
                </div>
                <div style={{ padding: '20px', background: 'var(--bg-primary)' }}>
                  <div style={{ fontWeight: 700, marginBottom: '8px' }}>Day 5 (17 Oct)</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Tanah Lot Temple sunset, Farewell Indian dinner (B/L/D)
                  </div>
                </div>
                <div style={{ padding: '20px', background: 'var(--bg-primary)' }}>
                  <div style={{ fontWeight: 700, marginBottom: '8px' }}>Day 6 (18 Oct)</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Hotel breakfast, check-out, airport transfer (B)
                  </div>
                </div>
              </div>

              {/* Inclusions/Exclusions */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '32px',
                marginBottom: '32px'
              }}>
                <div>
                  <h5 className="heading-3" style={{ marginBottom: '16px', color: '#25D366' }}>
                    Package Inclusions
                  </h5>
                  <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <li style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '2px', color: '#25D366' }} />
                      5 nights 3-star hotel (twin sharing)
                    </li>
                    <li style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '2px', color: '#25D366' }} />
                      Daily breakfast, lunch & dinner (Indian cuisine)
                    </li>
                    <li style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '2px', color: '#25D366' }} />
                      Airport transfers & private AC coach
                    </li>
                    <li style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '2px', color: '#25D366' }} />
                      English-speaking guide & Kecak Dance tickets
                    </li>
                    <li style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '2px', color: '#25D366' }} />
                      All entrance fees & sightseeing
                    </li>
                    <li style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '2px', color: '#25D366' }} />
                      Tourist levy, parking, toll fees, water
                    </li>
                    <li style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '2px', color: '#25D366' }} />
                      Driver & guide tips, taxes
                    </li>
                    <li style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '2px', color: '#25D366' }} />
                      1 FOC Tour Leader (room & all services)
                    </li>
                  </ul>
                </div>
                <div>
                  <h5 className="heading-3" style={{ marginBottom: '16px' }}>
                    Package Exclusions
                  </h5>
                  <ul style={{ listStyle: 'disc', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    <li>International airfare & visa/immigration charges</li>
                    <li>Travel insurance & personal expenses</li>
                    <li>Mini bar, laundry, additional tips</li>
                  </ul>
                  <h5 className="heading-3" style={{ marginTop: '24px', marginBottom: '12px' }}>
                    Payment Terms
                  </h5>
                  <ul style={{ listStyle: 'disc', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    <li>50% deposit upon confirmation</li>
                    <li>50% balance 30 days before arrival</li>
                    <li>Quote valid until 31 August 2026</li>
                  </ul>
                </div>
              </div>

              {/* CTA Buttons */}
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a
                  href="https://wa.me/6281517118799?text=Hello%20Indotravelista,%20I%20am%20a%20verified%20B2B%20Agent%20inquiring%20about%20the%20Bali%20Group%20Tariff."
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '14px 28px',
                    background: '#25D366',
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '15px',
                    fontWeight: 500,
                    borderRadius: 0
                  }}
                >
                  <FaWhatsapp size={18} />
                  WhatsApp DMC Direct Line
                </a>
                <button
                  className="btn-primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  onClick={() => alert('PDF download will be implemented')}
                >
                  <Download size={18} />
                  Download Partnership Deck (PDF)
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
